import React, { useEffect } from 'react'
import { Grid, makeStyles, Typography } from '@material-ui/core'
import { Trans, useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from '../../store/store.hooks'
import { Heading } from '@/components/Text'
import { GradientButton } from '@/components'
import { NavigateBack } from '@/components/Nav/NavigateBack'
import { FEATURES, MYMEDIO_URLS } from '../../constants'
import { normalizeLanguage } from '../../util'

const useStyles = makeStyles((theme) => ({
  crisisInterventionContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    // The padding based video container only gets its height in a single
    // line flex container, where its width is known up front
    flexWrap: 'nowrap',
    backgroundColor: '#fff',
    borderRadius: '15px',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: '20px',
    },
  },

  section: {
    margin: '1rem 5rem',
    color: '#1e2864',

    [theme.breakpoints.down('xs')]: {
      margin: '1rem 0rem',
    },
  },

  sectionTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },

  sectionText: {
    fontSize: '1rem',

    '& a': {
      color: 'inherit',
    },
  },

  videoContainer: {
    position: 'relative',
    width: '100%',
    paddingBottom: '56.25%' /* 16:9 aspect ratio */,
    height: 0,
    overflow: 'hidden',
  },

  videoPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
  },

  formButtonContainer: {
    width: '100%',
    maxWidth: '400px',
    margin: '0 auto 2rem auto',
  },
}))

const CrisisIntervention = () => {
  const classes = useStyles()
  const { i18n, t } = useTranslation('crisisIntervention')
  const isInternational = normalizeLanguage(i18n.language) !== 'hu'

  const { fetchSettings } = useStoreActions((actions) => actions.settings)
  const { settingsByKey } = useStoreState((state) => state.settings)
  const { accountFeaturesBySku } = useStoreState((state) => state.subscription)

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const formUrl =
    settingsByKey &&
    settingsByKey[
      isInternational
        ? MYMEDIO_URLS.CRISIS_INTERVENTION_FORM_INTL
        : MYMEDIO_URLS.CRISIS_INTERVENTION_FORM
    ]

  const therapistFeature = isInternational
    ? FEATURES.THERAPIST_APPOINTMENT_INTL
    : FEATURES.THERAPIST_APPOINTMENT
  const therapistUrl =
    accountFeaturesBySku &&
    accountFeaturesBySku[therapistFeature] &&
    settingsByKey &&
    settingsByKey[
      isInternational ? MYMEDIO_URLS.THERAPIST_INTL : MYMEDIO_URLS.THERAPIST
    ]

  const navigateToForm = () => {
    window.open(formUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Grid container xs={12} className={classes.crisisInterventionContainer}>
      <NavigateBack />
      <Heading>{t('crisisInterventionHeader')}</Heading>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {t('immediateSupportTitle')}
        </Typography>
        <Typography className={classes.sectionText}>
          <Trans
            t={t}
            i18nKey="immediateSupportText"
            components={{
              helplineLink: <a href="tel:116123" />,
              emergencyLink: <a href="tel:112" />,
            }}
          />
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {t('stressfulEventTitle')}
        </Typography>
        <Typography className={classes.sectionText}>
          {t('stressfulEventText')}
        </Typography>
      </div>

      <div className={classes.section}>
        <div className={classes.videoContainer}>
          <iframe
            src={t('videoUrl')}
            className={classes.videoPlayer}
            title={t('videoTitle')}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionText}>
          <Trans t={t} i18nKey="consultationGoal" components={{ b: <b /> }} />
          {therapistUrl && (
            <>
              {' '}
              <Trans
                t={t}
                i18nKey="generalCounselling"
                components={{
                  bookingLink: (
                    <a
                      href={therapistUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </>
          )}
        </Typography>
      </div>

      {formUrl && (
        <div className={classes.formButtonContainer}>
          <GradientButton onClick={navigateToForm}>
            {t('formButtonText')}
          </GradientButton>
        </div>
      )}
    </Grid>
  )
}

CrisisIntervention.displayName = 'CrisisIntervention'
export { CrisisIntervention }
export default CrisisIntervention
